import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronDownIcon, UserIcon, ScissorsIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Alert,
  Spinner,
  Badge,
  SlowOperationBanner,
  ConfirmationDialog,
  Modal,
} from '@/components/ui';
import { VideoSelector } from '@/components/video';
import { VideoTrimTool } from '@/components/phase/VideoTrimTool';
import PersonPicker from '@/components/evaluation/PersonPicker';
import EvaluationProgressCard from '@/components/evaluation/EvaluationProgressCard';
import { useTemplate } from '@/hooks';
import { useVideo } from '@/hooks/useVideos';
import { useVideoPersons } from '@/hooks/useVideoPersons';
import { useMyChannels, useCoachAthletes, useAthleteCoaches } from '@/hooks/useChannels';
import { useEvaluationJob } from '@/hooks/useEvaluationJob';
import { usePendingJobs } from '@/hooks/usePendingJobs';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation } from '@tanstack/react-query';
import { formatDate } from '@/utils/formatters';
import { classifySubmitError, type SubmitErrorResult } from './classifySubmitError';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import {
  submitEvaluation,
  preflightEvaluation,
  getFormationSuggestions,
  submitTeamDrill,
} from '@/services/evaluationApi';
import type {
  FormationPersonSuggestion,
  PositionalAssignment,
  TeamDrillSubmitResponse,
} from '@/services/evaluationApi';
import type { EvaluationRequest } from '@/types/api';
import PositionalLabelPicker from '@/components/evaluation/PositionalLabelPicker';
import { useMyChannelCreditBalance } from '@/hooks/useCreditBalance';
import { CreditBalanceWidget } from '@/components/credits/CreditBalanceWidget';
import CaptureGuidanceCard from '@/components/evaluation/CaptureGuidanceCard';
import { mapQualityError } from '@/utils/qualityGateErrors';

// ─── sessionStorage key helpers ──────────────────────────────────────────────

function storageKey(templateId: string, videoId: string): string {
  return `eval-job:${templateId}:${videoId}`;
}

function readStoredJobId(templateId: string, videoId: string): string | null {
  try {
    return sessionStorage.getItem(storageKey(templateId, videoId));
  } catch {
    return null;
  }
}

function writeStoredJobId(templateId: string, videoId: string, jobId: string): void {
  try {
    sessionStorage.setItem(storageKey(templateId, videoId), jobId);
  } catch {
    // sessionStorage unavailable — degrade gracefully
  }
}

function clearStoredJobId(templateId: string, videoId: string): void {
  try {
    sessionStorage.removeItem(storageKey(templateId, videoId));
  } catch {
    // ignore
  }
}

// ─── Error message helpers ───────────────────────────────────────────────────
// SubmitErrorResult, classifySubmitError moved to classifySubmitError.ts
// (react-refresh/only-export-components).

// Camera views recognised by the backend's resolve_effective_camera_view().
// The coach-set metadata.camera_angle_setup wins over the auto-detected
// metadata.camera_view; "other"/unrecognised values fall through.
const RECOGNISED_CAMERA_VIEWS = ['side', 'frontal', 'diagonal'];

// ─── Component ───────────────────────────────────────────────────────────────

function EvaluatePerformance() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const { data: template, isLoading: isLoadingTemplate } = useTemplate(templateId);

  const [athleteVideoId, setAthleteVideoId] = useState<string>('');
  const [selectedPersonIndex, setSelectedPersonIndex] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<SubmitErrorResult | null>(null);

  // ─── Positional team-drill state (PTD-6) ──────────────────────────────────
  // Whether the selected template is a positional template
  const isPositional = template?.template_kind === 'positional';
  // Formation suggestions fetched from POST /api/evaluate/formation-suggest
  const [formationPersons, setFormationPersons] = useState<FormationPersonSuggestion[]>([]);
  const [templatePositionLabels, setTemplatePositionLabels] = useState<string[]>([]);
  const [formationSuggestError, setFormationSuggestError] = useState<string | null>(null);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  // Confirmed assignments from PositionalLabelPicker; null until coach confirms
  const [positionAssignments, setPositionAssignments] = useState<PositionalAssignment[] | null>(null);

  // "Share with coach" state — any user can share an evaluation with a coach.
  // Initialized from ?share_with= query param to support deep-linking.
  const [sharedWithUserId, setSharedWithUserId] = useState<string | null>(
    searchParams.get('share_with')
  );

  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  // "Evaluate for an athlete" state (Goal E) — coach/admin only. The result
  // belongs to the chosen athlete, not the submitting coach. Mutually
  // exclusive with sharedWithUserId (the backend 400s if both are set), so
  // choosing one always clears the other.
  const [athleteUserId, setAthleteUserIdState] = useState<string | null>(null);
  const setAthleteUserId = (value: string | null) => {
    setAthleteUserIdState(value);
    if (value) setSharedWithUserId(null);
  };

  // S28: derive the template's channel slug (available after template loads).
  // Used to fetch the credit balance and to build the S27 buy-credits deep-link.
  const templateChannelSlug = template?.channel_slug ?? null;

  // S26 balance hook — self-read only, degrades gracefully on error/unavailable.
  // membership_status from this response is the canonical approved-membership signal
  // (resolves Open Q4 — no separate membership query needed).
  const { data: creditBalance, isLoading: isCreditBalanceLoading } =
    useMyChannelCreditBalance(templateChannelSlug);

  // S28 pre-submit zero-balance gate:
  // Block submit ONLY when balance is KNOWN zero (both buckets === 0) — treat
  // "S26 data present and known-zero" as the active-enforcement signal (Open Q3).
  // Never block when loading/unknown/no data (avoids false-positive gating).
  //
  // Two hard exemptions mirror the backend submit path (evaluation_jobs.py):
  //   1. enforcement_active === false → the credit ledger is OFF and the legacy
  //      monthly-budget path governs evals; the balance is always 0/0 and must
  //      NOT gate. Without this the dark ledger blocks Evaluate for everyone.
  //   2. admins bypass credit + budget checks unconditionally on the server, so
  //      they must never be button-blocked client-side.
  const isKnownZeroBalance =
    !isCreditBalanceLoading &&
    creditBalance !== undefined &&
    creditBalance.enforcement_active === true &&
    user?.role !== 'admin' &&
    creditBalance.included_available === 0 &&
    creditBalance.purchased_available === 0;

  // Approved-membership signal from S26 (used for CTA gating in this surface).
  const isMemberApproved = creditBalance?.membership_status === 'approved';

  // Fetch channels for the share-with-coach / evaluate-for-an-athlete dropdowns.
  // Coaches use useCoachAthletes to find athletes in channels they own/administer
  // (U-2 — the only member list a coach can read today).
  // Athletes use useAthleteCoaches to find coaches in their channels.
  const { data: myChannels = [] } = useMyChannels();
  const channelSlugs = useMemo(() => myChannels.map((c) => c.slug), [myChannels]);
  // F-2 (red team): GET /channels/{slug}/members is owner/channel_admin-only
  // (backend_routes.py _require_owner_or_channel_admin). /users/me/channels
  // returns the UNION of owned + joined channels, so passing every slug here
  // hits a 403 for any channel this coach only belongs to (not owns) — and
  // because useCoachAthletes used Promise.all, that single 403 emptied the
  // whole picker. Scope the picker to channels this coach actually owns;
  // useCoachAthletes also now tolerates a stray 403 (Promise.allSettled) as
  // a second layer of defense.
  const ownedChannelSlugs = useMemo(
    () => myChannels.filter((c) => c.owner_id === user?.id).map((c) => c.slug),
    [myChannels, user?.id]
  );
  const currentUserIdArr = user?.id ? [user.id] : [];
  const { data: channelMembers = [] } = useCoachAthletes(
    isCoachOrAdmin ? ownedChannelSlugs : [],
    currentUserIdArr
  );
  // "Evaluate for an athlete" picker (Goal E, E3) — athlete-role members only.
  const coachAthletes = useMemo(
    () => channelMembers.filter((member) => member.role === 'athlete'),
    [channelMembers]
  );
  // Coaches in the athlete's channels — used in the share-with dropdown.
  const { data: channelCoaches = [] } = useAthleteCoaches(
    !isCoachOrAdmin ? channelSlugs : []
  );

  // True for the 1.5 s window between upload completion and when we start
  // fetching /persons. Pose extraction runs synchronously on the backend
  // for ~2-5 s; we delay the videoId state set to avoid a racing 404/error.
  const [isProcessing, setIsProcessing] = useState(false);

  // Phase 2 declarative handedness — lead side override disclosure panel
  const [leadSideOverrideOpen, setLeadSideOverrideOpen] = useState(false);
  const [leadSideOverride, setLeadSideOverride] = useState<'left' | 'right' | ''>('');

  // Capture guidance card — force re-show when user taps "Try again" after a
  // quality-gate failure so tips appear alongside the empty video selector.
  const [guidanceForceOpen, setGuidanceForceOpen] = useState(false);

  // ─── Athlete video trim (B-2026-0104) ─────────────────────────────────────
  // Collapsed by default — the common path (no trim) is visually unchanged.
  const [trimCardOpen, setTrimCardOpen] = useState(false);
  const [athleteTrimStart, setAthleteTrimStart] = useState(0);
  const [athleteTrimEnd, setAthleteTrimEnd] = useState<number | null>(null);
  // Hidden <video> ref — used as a fallback when the Video API metadata is not yet loaded.
  const metaVideoRef = useRef<HTMLVideoElement>(null);
  // Fallback frame count derived from the hidden <video> element's duration.
  const [fallbackTotalFrames, setFallbackTotalFrames] = useState(0);

  // Reset trim state whenever the user selects a new video.
  const resetTrimState = useCallback(() => {
    setAthleteTrimStart(0);
    setAthleteTrimEnd(null);
    setFallbackTotalFrames(0);
    setTrimCardOpen(false);
  }, []);

  // Active job ID — restored from sessionStorage on mount (F1, refresh recovery)
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // ─── F1: Mount-time job discovery ───────────────────────────────────────
  // Step A: check sessionStorage immediately (fast, no network).
  // Step B: if sessionStorage is empty, check server-side pending jobs list.
  const { data: pendingJobs } = usePendingJobs();

  useEffect(() => {
    if (!templateId || !athleteVideoId) return;

    // Step A: sessionStorage hit
    const stored = readStoredJobId(templateId, athleteVideoId);
    if (stored) {
      setActiveJobId(stored);
      return;
    }

    // Step B: server-side discovery — not possible without request_payload in
    // the response (backend deferred). sessionStorage is the primary mechanism.
    // pendingJobs is still consumed by EvaluationsList for the global pending section.
  }, [templateId, athleteVideoId, pendingJobs]);

  // ─── Positional formation suggestions (PTD-6) ────────────────────────────
  // When a video is selected for a positional template, fetch formation suggestions.
  // Fires whenever athleteVideoId or isPositional changes (video swap or template swap).
  useEffect(() => {
    if (!athleteVideoId || !templateId || !isPositional || isProcessing) return;

    let cancelled = false;
    setIsFetchingSuggestions(true);
    setFormationSuggestError(null);
    setFormationPersons([]);
    setTemplatePositionLabels([]);
    setPositionAssignments(null);

    getFormationSuggestions({ athlete_video_id: athleteVideoId, template_id: templateId })
      .then((resp) => {
        if (cancelled) return;
        setFormationPersons(resp.persons);
        setTemplatePositionLabels(resp.template_position_labels);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof Error
            ? err.message
            : 'Could not fetch formation suggestions. Please try again.';
        setFormationSuggestError(msg);
      })
      .finally(() => {
        if (!cancelled) setIsFetchingSuggestions(false);
      });

    return () => {
      cancelled = true;
    };
  // isPositional and isProcessing are derived from template/athleteVideoId — stable enough;
  // templateId and athleteVideoId are the only values that should re-trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteVideoId, templateId, isPositional]);

  // ─── Job polling ─────────────────────────────────────────────────────────
  const jobQuery = useEvaluationJob(activeJobId);
  const job = jobQuery.data;

  // PS-0a (score-fast split) — navigate as soon as the score is ready, not
  // only when coaching has finished. `scoreReady` is true at status 'scored'
  // OR 'completed'. EvaluationResults polls separately for the coaching
  // narrative so the user sees the score immediately without waiting ~120s.
  //
  // Frontend #18 — navigation is owned by the call site, not the hook.
  const { scoreReady, resultEvaluationId } = jobQuery;
  useEffect(() => {
    if (scoreReady && resultEvaluationId) {
      navigate(`/evaluations/${resultEvaluationId}`);
    }
  }, [scoreReady, resultEvaluationId, navigate]);

  // Clear sessionStorage when the job reaches a score-ready state.
  // (Previously cleared only on 'completed'; clearing at 'scored' is safe
  // because we've already navigated away and the eval row exists.)
  useEffect(() => {
    if (
      (job?.status === 'scored' || job?.status === 'completed') &&
      templateId &&
      athleteVideoId
    ) {
      clearStoredJobId(templateId, athleteVideoId);
    }
  }, [job?.status, templateId, athleteVideoId]);

  // ─── Submit mutation ──────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  // B-2026-0089 Phase 3 — pre-submit visibility probe state.
  // isPreflighting drives the "Checking your video…" button label; the warning
  // holds the accidental (off-frame) regions + the exact submit payload so the
  // "Submit anyway" button can proceed with the SAME request the user built.
  const [isPreflighting, setIsPreflighting] = useState(false);
  const [partialBodyWarning, setPartialBodyWarning] = useState<{
    regions: string[];
    // Stance Orientation Tier 3 — backend-controlled orientation message. null
    // when the only warning is partial-body (no orientation issue detected).
    orientationMessage: string | null;
    // "info" = confident opposite-side mismatch that the pipeline auto-corrected;
    // "advisory" = low-confidence detection, athlete should double-check.
    // null when there is no orientation message.
    orientationSeverity: 'info' | 'advisory' | null;
    payload: EvaluationRequest;
  } | null>(null);

  // Solo submit mutation (unchanged path)
  const submitMutation = useMutation({
    mutationFn: submitEvaluation,
    onMutate: () => {
      setIsSubmitting(true);
      setSubmitError(null);
    },
    onSuccess: (data) => {
      if (templateId && athleteVideoId) {
        writeStoredJobId(templateId, athleteVideoId, data.job_id);
      }
      setActiveJobId(data.job_id);
      setIsSubmitting(false);
    },
    onError: (error: unknown) => {
      const classified = classifySubmitError(error);
      setSubmitError(classified);
      setIsSubmitting(false);

      if (classified.isAuth) {
        // Give the user a moment to read the message, then redirect.
        setTimeout(() => navigate('/login'), 2000);
      }
    },
  });

  // Positional (team-drill) submit mutation (PTD-6)
  // On success, navigate to the first child job's result or show a submitted state.
  // TODO(PTD-10): replace navigation with the full team leaderboard page when built.
  const teamDrillMutation = useMutation<TeamDrillSubmitResponse, unknown, Parameters<typeof submitTeamDrill>[0]>({
    mutationFn: submitTeamDrill,
    onMutate: () => {
      setIsSubmitting(true);
      setSubmitError(null);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      // PTD-10: navigate to the team-drill leaderboard.
      navigate(`/team-drill/${data.parent_job_id}`);
    },
    onError: (error: unknown) => {
      const classified = classifySubmitError(error);
      setSubmitError(classified);
      setIsSubmitting(false);

      if (classified.isAuth) {
        setTimeout(() => navigate('/login'), 2000);
      }
    },
  });

  // Fetch person detection data for the selected video
  const { data: videoPersons } = useVideoPersons(athleteVideoId || undefined);

  // Fetch video metadata for trim UI (fps + frame_count)
  const { data: videoMeta } = useVideo(athleteVideoId || undefined);

  // Media-auth security sprint (2026-09-21): /videos/{id}/stream now requires
  // a bearer token or a signed sig/exp pair — a bare <video src> sends
  // neither. streamUrl is null while the media-token fetch is in flight.
  const { streamUrl: athleteVideoStreamUrl } = useMediaUrl(athleteVideoId || undefined);

  // Prefer server-provided values; fall back to the hidden-video measurement.
  const videoFps = videoMeta?.fps ?? 30;
  const videoTotalFrames =
    videoMeta?.frame_count ??
    (videoMeta?.duration ? Math.round(videoMeta.duration * videoFps) : fallbackTotalFrames);

  const handleVideoSelect = (videoId: string) => {
    setIsProcessing(true);
    // Reset job state and trim state when the user picks a different video
    setActiveJobId(null);
    setSubmitError(null);
    resetTrimState();
    // Reset positional state (PTD-6)
    setFormationPersons([]);
    setTemplatePositionLabels([]);
    setFormationSuggestError(null);
    setPositionAssignments(null);
    setTimeout(() => {
      setAthleteVideoId(videoId);
      setSelectedPersonIndex(null);
      setIsProcessing(false);
    }, 1500);
  };

  // When multiple people are in the video the user must pick one before evaluating
  // (solo path only — positional path uses PositionalLabelPicker instead).
  const needsPersonSelection =
    !isPositional &&
    videoPersons !== undefined &&
    videoPersons.person_count > 1 &&
    selectedPersonIndex === null;

  // Positional path requires coach to confirm assignments before evaluating
  const needsPositionalConfirmation =
    isPositional && athleteVideoId && !positionAssignments && !isFetchingSuggestions;

  // ─── Frontal-template handedness requirement ──────────────────────────────
  // Frontal-view lead-side detection is unreliable (experimentally shown to be
  // confidently inverted), so frontal non-symmetric templates REQUIRE an
  // athlete side declaration (profile handedness or the per-eval override)
  // before submit. Required-field validation only — NOT a scoring gate; the
  // backend still accepts undeclared submissions (it returns an advisory).
  const rawCoachCameraView = (template?.metadata?.camera_angle_setup ?? '')
    .trim()
    .toLowerCase();
  // B3 — legacy templates can carry the retired `front` spelling, which this
  // read point never recognised; it fell through to the auto-detected
  // camera_view and the frontal handedness requirement silently did not fire.
  // Normalise it here the same way the backend's single read point does.
  const coachCameraView = rawCoachCameraView === 'front' ? 'frontal' : rawCoachCameraView;
  const effectiveCameraView = RECOGNISED_CAMERA_VIEWS.includes(coachCameraView)
    ? coachCameraView
    : template?.metadata?.camera_view ?? null;
  const frontalHandednessRequired =
    effectiveCameraView === 'frontal' && template?.exercise_symmetry !== 'symmetric';

  // Middle of the coach's trimmed reference window — the frame the capture
  // card shows, where the movement is most recognisable. Falls back to the
  // first frame when the template carries no trim range.
  const referenceMidFrame =
    typeof template?.trim_start_frame === 'number' &&
    typeof template?.trim_end_frame === 'number' &&
    template.trim_end_frame >= template.trim_start_frame
      ? Math.round((template.trim_start_frame + template.trim_end_frame) / 2)
      : 0;
  // "ambidextrous" counts as undeclared — the backend treats it as unknown.
  const hasDeclaredSide =
    leadSideOverride !== '' || user?.handedness === 'left' || user?.handedness === 'right';
  const needsHandednessSelection = frontalHandednessRequired && !hasDeclaredSide;

  // Auto-open the lead-side panel when the template requires a declaration so
  // the required selector is visible without hunting for the "Advanced" toggle.
  useEffect(() => {
    if (frontalHandednessRequired) setLeadSideOverrideOpen(true);
  }, [frontalHandednessRequired]);

  // F9 — Only offer retry when job failed, is retryable, and there's no other
  // active job for the same (template, video) combination.
  const canRetry =
    job?.status === 'failed' &&
    (job.error_retryable || false) &&
    !isSubmitting &&
    activeJobId !== null;

  const handleEvaluate = async () => {
    if (!athleteVideoId || !templateId) return;
    if (isSubmitting || isPreflighting || (job && job.status !== 'failed')) return;
    // Required-field validation: frontal templates need a declared side.
    if (needsHandednessSelection) return;

    // Clear stale failed job from state so we get a fresh poll
    if (job?.status === 'failed' && templateId && athleteVideoId) {
      clearStoredJobId(templateId, athleteVideoId);
      setActiveJobId(null);
    }

    // ── Positional path (PTD-6) ────────────────────────────────────────────
    if (isPositional) {
      if (!positionAssignments) return; // picker not yet confirmed
      teamDrillMutation.mutate({
        athlete_video_id: athleteVideoId,
        template_id: templateId,
        position_assignments: positionAssignments,
        lead_side_override: leadSideOverride || null,
        shared_with_user_id: sharedWithUserId ?? undefined,
        // Goal E: "evaluate for an athlete" is solo-only; the API answers 400 if
        // this is sent on a team drill, so it is never sent here and the picker
        // is hidden for positional templates.
      });
      return;
    }

    // ── Solo path ──────────────────────────────────────────────────────────
    if (needsPersonSelection) return;

    // Only send non-default trim values so the backend stays byte-identical
    // when no trim is applied (null = full video).
    const trimStart = athleteTrimStart > 0 ? athleteTrimStart : null;
    const trimEnd =
      athleteTrimEnd !== null && videoTotalFrames > 0 && athleteTrimEnd < videoTotalFrames - 1
        ? athleteTrimEnd
        : null;

    const payload: EvaluationRequest = {
      template_id: templateId,
      athlete_video_id: athleteVideoId,
      include_explanation: true,
      focal_person_index: selectedPersonIndex ?? undefined,
      lead_side_override: leadSideOverride || null,
      shared_with_user_id: sharedWithUserId ?? undefined,
      athlete_user_id: athleteUserId ?? undefined,
      athlete_trim_start_frame: trimStart,
      athlete_trim_end_frame: trimEnd,
    };

    // B-2026-0089 Phase 3 — NON-BLOCKING pre-submit visibility probe.
    // If a template-scored body region is off-frame in the athlete's video,
    // warn BEFORE spending the evaluation so they can re-record. This must never
    // block a good submission: checked=false, no accidental regions, or ANY
    // probe error all fall through to a normal submit.
    setIsPreflighting(true);
    let preflight: Awaited<ReturnType<typeof preflightEvaluation>> | null = null;
    try {
      preflight = await preflightEvaluation({
        template_id: templateId,
        athlete_video_id: athleteVideoId,
        shared_with_user_id: sharedWithUserId ?? undefined,
        athlete_user_id: athleteUserId ?? undefined,
        // Forward the lead-side override so the backend skips the Stance
        // Orientation check when the athlete has explicitly declared their side.
        lead_side_override: leadSideOverride || null,
      });
    } catch {
      preflight = null; // best-effort — never block on the probe
    }
    setIsPreflighting(false);

    // Advisory pre-flight warnings (never block): partial-body regions and/or a
    // Stance Orientation mismatch. If either is present, show the confirm dialog.
    // When the only trigger is an "info"-severity orientation note (auto-corrected,
    // high confidence), the dialog is reassuring — no "Re-record" CTA.
    const accidentalRegions =
      preflight?.checked && preflight.partial_body
        ? preflight.partial_body.accidental_regions
        : [];
    const orientationMessage =
      preflight?.checked && preflight.orientation
        ? preflight.orientation.message
        : null;
    const orientationSeverity: 'info' | 'advisory' | null =
      preflight?.checked && preflight.orientation
        ? preflight.orientation.severity
        : null;

    if (accidentalRegions.length > 0 || orientationMessage) {
      setPartialBodyWarning({
        regions: accidentalRegions,
        orientationMessage,
        orientationSeverity,
        payload,
      });
      return;
    }

    submitMutation.mutate(payload);
  };

  // ─── Derived display state ───────────────────────────────────────────────

  // F8 — Progress percentage: show 1% ("Sending...") in the gap between
  // submit returning and the first poll resolving.
  const displayProgress = (() => {
    if (isSubmitting) return 1;
    if (!job) return 0;
    return job.progress_pct ?? (job.status === 'processing' ? 10 : 0);
  })();

  const isInProgress = isSubmitting || (job !== undefined && job !== null && job.status !== 'failed' && job.status !== 'completed');

  // ─── Render ──────────────────────────────────────────────────────────────

  if (isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label="Loading template..." />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <Alert variant="error">Template not found</Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Evaluate Performance"
        description={`Using template: ${template.name}`}
        showBack
      />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Step 1: Template Info */}
        <Card>
          <CardHeader>
            <CardTitle>Template Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {template.movement_type && (
                  <div>
                    <span className="text-text-secondary">Movement Type:</span>
                    <p className="font-medium mt-1 text-text-primary">{template.movement_type}</p>
                  </div>
                )}
                <div>
                  <span className="text-text-secondary">Created:</span>
                  <p className="font-medium mt-1 text-text-primary">{formatDate(template.created_at)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {template.phases && template.phases.length > 0 && (
                  <Badge variant="secondary">
                    {template.phases.length} phases
                  </Badge>
                )}
                {template.regions && template.regions.length > 0 && (
                  <Badge variant="secondary">
                    {template.regions.length} regions
                  </Badge>
                )}
                {template.annotations && template.annotations.length > 0 && (
                  <Badge variant="info">
                    {template.annotations.length} annotations
                  </Badge>
                )}
              </div>

              {template.description && (
                <div>
                  <span className="text-sm text-text-secondary">Description:</span>
                  <p className="text-sm mt-1 text-text-primary">{template.description}</p>
                </div>
              )}

              {template.coach_notes && (
                <div>
                  <span className="text-sm text-text-secondary">Coach Notes:</span>
                  <p className="text-sm mt-1 bg-info/10 p-3 rounded-sm-control border border-border-subtle text-text-primary">
                    {template.coach_notes}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Camera Setup Guide */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
              <CardTitle>Camera Setup Guide</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(template.metadata?.camera_device || template.metadata?.camera_angle_setup) && (
                <div className="bg-info/10 border border-border-subtle rounded-sm-control p-3 text-sm text-text-primary space-y-1">
                  <p className="font-medium text-text-primary">This template was recorded with:</p>
                  <ul className="space-y-1 text-text-secondary">
                    {template.metadata.camera_device && (
                      <li>
                        Device:{' '}
                        <span className="text-text-primary capitalize">
                          {template.metadata.camera_device.replace('_', ' ')}
                        </span>
                        {template.metadata.camera_device === 'smartphone' &&
                          template.metadata.camera_orientation && (
                            <span className="text-text-primary">
                              {' '}
                              ({template.metadata.camera_orientation})
                            </span>
                          )}
                      </li>
                    )}
                    {template.metadata.camera_angle_setup && (
                      <li>
                        Camera angle:{' '}
                        <span className="text-text-primary capitalize">
                          {template.metadata.camera_angle_setup}
                        </span>{' '}
                        view
                      </li>
                    )}
                  </ul>
                  <p className="text-text-secondary pt-1">
                    For best results, film your video from the same position.
                  </p>
                </div>
              )}

              <div className="text-sm text-text-secondary space-y-2">
                <p>
                  Matching the camera angle ensures accurate scoring. Different angles can cause
                  the AI to misread body positions.
                </p>
                <ul className="space-y-1.5 pl-0">
                  {[
                    'Match the camera angle shown in the reference video',
                    'Keep the camera at roughly the same height as the reference',
                    'Ensure your full body is visible throughout the movement',
                    template.metadata?.camera_orientation === 'landscape'
                      ? 'Film in landscape (horizontal) orientation to match the reference'
                      : 'Use a stable filming position — avoid handheld shaking',
                  ].map((tip) => (
                    <li key={tip} className="flex items-start gap-2">
                      <span className="text-accent mt-0.5 shrink-0">-</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {template.metadata?.performance_notes &&
                template.metadata.performance_notes.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border-subtle">
                    <h4 className="text-sm font-semibold text-text-primary mb-2">
                      Performance Requirements
                    </h4>
                    <p className="text-xs text-text-secondary mb-2">
                      Your coach requires the following when performing this movement:
                    </p>
                    <ul className="space-y-1.5">
                      {template.metadata.performance_notes.map((note: string, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                          <span className="text-amber-400 mt-0.5 flex-shrink-0">●</span>
                          {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Select or Upload Athlete Video — disabled while a job is active */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Athlete Video</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Capture guidance card — shown before file selection, for every
                camera view, whenever the template declares which way the
                performer faces in the demo. Shows a still from the coach's
                reference video so "the same side toward the camera" is
                something the athlete can see rather than decode. */}
            {!athleteVideoId && !isInProgress && (
              <CaptureGuidanceCard
                facingDirection={template.metadata?.facing_direction ?? template.facing_direction ?? null}
                templateId={template.id}
                referenceVideoId={template.reference_video_id}
                midFrame={referenceMidFrame}
                forceOpen={guidanceForceOpen}
                onDismiss={() => setGuidanceForceOpen(false)}
                className="mb-4"
              />
            )}
            <Alert variant="info" className="mb-4">
              Upload a video of the athlete performing the same movement. The AI will compare it
              to the reference template. Keep the camera{' '}
              <strong>level, stable, and at the same angle</strong> as the reference video.
            </Alert>
            <VideoSelector
              onSelect={handleVideoSelect}
              selectedVideoId={athleteVideoId}
            />
          </CardContent>
        </Card>

        {/* Hidden video element — fallback metadata source when the Video API
            response hasn't loaded yet.  Only fires setFallbackTotalFrames when
            the server-provided frame_count is absent. Gated on
            athleteVideoStreamUrl (signed) — never emits the unsigned URL,
            which would 401 (media-auth security sprint, 2026-09-21). */}
        {athleteVideoId && !videoMeta?.frame_count && athleteVideoStreamUrl && (
          <video
            ref={metaVideoRef}
            src={athleteVideoStreamUrl}
            style={{ display: 'none' }}
            preload="metadata"
            onLoadedMetadata={() => {
              const vid = metaVideoRef.current;
              if (!vid || vid.duration <= 0) return;
              const fps = videoMeta?.fps ?? 30;
              setFallbackTotalFrames(Math.round(vid.duration * fps));
            }}
            aria-hidden="true"
          />
        )}

        {/* Trim clip — optional, collapsed by default so the common path is unchanged */}
        {athleteVideoId && !isInProgress && (
          <div
            className="rounded-lg border border-border-subtle bg-dark-elevated-1 overflow-hidden"
            data-testid="trim-card"
          >
            {/* Toggle header */}
            <button
              type="button"
              onClick={() => setTrimCardOpen((prev) => !prev)}
              aria-expanded={trimCardOpen}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-text-secondary hover:bg-dark-elevated-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
              data-testid="trim-card-toggle"
            >
              <div className="flex items-center gap-2">
                <ScissorsIcon className="h-4 w-4 flex-shrink-0 text-amber-400" aria-hidden="true" />
                <span>Trim clip (optional)</span>
                {/* Show an active badge when a non-default trim is set */}
                {(athleteTrimStart > 0 || athleteTrimEnd !== null) && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30"
                    data-testid="trim-active-badge"
                  >
                    active
                  </span>
                )}
              </div>
              <ChevronDownIcon
                className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${trimCardOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {trimCardOpen && (
              <div className="border-t border-border-subtle">
                <div className="px-4 pt-3 pb-1">
                  <p className="text-xs text-text-muted leading-relaxed">
                    Remove the lead-in or cool-down that isn't part of the movement — only the clip
                    you set is scored.
                  </p>
                  {/* Lead-side hint: shown when a trim is active and the template is
                      asymmetric with no declared lead side (RISK-03 / Q1).
                      exercise_symmetry is checked directly — if absent the condition
                      falls through (safe). */}
                  {(athleteTrimStart > 0 || athleteTrimEnd !== null) &&
                    template.exercise_symmetry === 'asymmetric' &&
                    !leadSideOverride && (
                      <p
                        className="mt-2 text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5 leading-relaxed"
                        data-testid="lead-side-hint"
                      >
                        For asymmetric movements, set Lead Side for accurate scoring when trimming.
                      </p>
                    )}
                </div>

                {videoTotalFrames > 0 && athleteVideoStreamUrl ? (
                  <div className="p-4 pt-2">
                    <VideoTrimTool
                      videoUrl={athleteVideoStreamUrl}
                      totalFrames={videoTotalFrames}
                      fps={videoFps}
                      trimStart={athleteTrimStart}
                      trimEnd={athleteTrimEnd ?? videoTotalFrames - 1}
                      onTrimChange={(s, e) => {
                        setAthleteTrimStart(s);
                        setAthleteTrimEnd(e);
                      }}
                      hideConfirm
                    />
                    {/* Reset to full video */}
                    {(athleteTrimStart > 0 || athleteTrimEnd !== null) && (
                      <button
                        type="button"
                        onClick={() => {
                          setAthleteTrimStart(0);
                          setAthleteTrimEnd(null);
                        }}
                        className="mt-2 text-xs text-text-muted hover:text-text-secondary underline underline-offset-2 transition-colors"
                        data-testid="trim-reset-full-video"
                      >
                        Remove trim / use full video
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="px-4 pb-4 pt-2">
                    <p className="text-xs text-text-muted">
                      Loading video metadata…
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Positional template path (PTD-6) ─────────────────────────────────── */}
        {isPositional && athleteVideoId && !isInProgress && (
          <>
            {isFetchingSuggestions && (
              <div className="flex items-center gap-2 text-sm text-text-secondary py-2">
                <Spinner size="sm" />
                <span>Detecting players and matching positions...</span>
              </div>
            )}

            {formationSuggestError && !isFetchingSuggestions && (
              <Alert variant="error">
                <p className="font-medium">Could not load formation suggestions</p>
                <p className="text-sm mt-1">{formationSuggestError}</p>
              </Alert>
            )}

            {!isFetchingSuggestions && formationPersons.length > 0 && !positionAssignments && (
              <PositionalLabelPicker
                persons={formationPersons}
                templateLabels={templatePositionLabels}
                onConfirm={(assignments) => setPositionAssignments(assignments)}
              />
            )}

            {positionAssignments && !isInProgress && (
              <div
                className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-400"
                data-testid="positional-assignments-confirmed"
              >
                <p className="font-medium">Position assignments confirmed</p>
                <ul className="mt-1 text-xs text-green-400/80 space-y-0.5">
                  {positionAssignments.map((a) => (
                    <li key={a.person_index}>
                      Person {a.person_index + 1} → {a.position_label}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setPositionAssignments(null)}
                  className="mt-2 text-xs text-green-400/60 hover:text-green-400 underline underline-offset-2 transition-colors"
                >
                  Change assignments
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Solo path: Person Picker — shown only when multiple people are detected ── */}
        {!isPositional && athleteVideoId && videoPersons && videoPersons.person_count > 1 && (
          <>
            {/* Face-privacy warning — all_persons_faceless means the server could not
                filter by face visibility and returned the full list as an exemption.
                We warn the user but do not block evaluation. */}
            {videoPersons.all_persons_faceless && (
              <div data-testid="all-persons-faceless-warning">
                <Alert variant="warning">
                  No clear face detected in this video — make sure the camera faces the athlete.
                </Alert>
              </div>
            )}
            <PersonPicker
              videoId={athleteVideoId}
              persons={videoPersons.persons}
              sampleFrameUrl={videoPersons.sample_frame_url}
              selectedIndex={selectedPersonIndex}
              onSelect={setSelectedPersonIndex}
              label="Select the athlete to evaluate"
            />
          </>
        )}

        {/* Evaluate for an athlete (Goal E) — coach/admin only. Mutually exclusive
            with "Share evaluation with" below: picking an athlete here hides
            that card, since the backend rejects a request that sets both. */}
        {isCoachOrAdmin && !isPositional && coachAthletes.length > 0 && !sharedWithUserId && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-text-secondary" />
                <CardTitle>Evaluate for an athlete</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <select
                  value={athleteUserId ?? ''}
                  onChange={(e) =>
                    setAthleteUserId(e.target.value === '' ? null : e.target.value)
                  }
                  disabled={isInProgress}
                  className="w-full rounded-sm-control border border-border-subtle bg-dark-elevated-1 text-text-primary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Evaluate for an athlete"
                >
                  <option value="">Evaluate for myself</option>
                  {coachAthletes.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.display_name ?? member.email}
                    </option>
                  ))}
                </select>
                {athleteUserId && (
                  <p className="text-xs text-text-muted">
                    The result belongs to the athlete. They decide whether it goes on the
                    leaderboard; you keep access to it and the evaluation uses your budget.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Share evaluation with coach — available to all users in a channel */}
        {channelCoaches.length > 0 && !athleteUserId && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-text-secondary" />
                <CardTitle>Share evaluation with</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <select
                  value={sharedWithUserId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : e.target.value;
                    setSharedWithUserId(value);
                    if (value) setAthleteUserIdState(null);
                  }}
                  disabled={isInProgress}
                  className="w-full rounded-sm-control border border-border-subtle bg-dark-elevated-1 text-text-primary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Share evaluation with coach"
                >
                  <option value="">Don't share</option>
                  {channelCoaches.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.display_name ?? member.email}
                    </option>
                  ))}
                </select>
                {sharedWithUserId && (
                  <p className="text-xs text-text-muted">
                    Your coach will be notified when the evaluation is ready and can comment on the results.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lead Side Override — hidden for symmetric templates */}
        {template.exercise_symmetry !== 'symmetric' && (
          <div className="rounded-lg border border-border-subtle bg-dark-elevated-1 overflow-hidden">
            <button
              type="button"
              onClick={() => setLeadSideOverrideOpen((prev) => !prev)}
              aria-expanded={leadSideOverrideOpen}
              disabled={isInProgress}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-text-secondary hover:bg-dark-elevated-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {/* Coach-facing copy standard §3: the old label
                  "Advanced: Override lead side for this evaluation" named a
                  field, not a question the athlete can answer. */}
              <span>
                {frontalHandednessRequired
                  ? 'Which side leads the movement? (needed for this video)'
                  : 'Which side leads the movement?'}
              </span>
              <ChevronDownIcon
                className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${leadSideOverrideOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {leadSideOverrideOpen && (
              <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border-subtle">
                {needsHandednessSelection && (
                  <p
                    className="text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5 leading-relaxed"
                    data-testid="frontal-handedness-hint"
                  >
                    This demo was filmed from the front, where we can't tell your left from
                    your right. Pick the side that leads so your video is compared the right
                    way round.
                  </p>
                )}
                <p className="text-xs text-text-muted leading-relaxed">
                  Usually we detect this; change it only if the video was flagged wrong. Your
                  choice applies to this video only — "Use my usual side" keeps the setting
                  from your profile.
                </p>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { value: '' as const, label: 'Use my usual side' },
                      { value: 'left' as const, label: 'Left leads' },
                      { value: 'right' as const, label: 'Right leads' },
                    ] as const
                  ).map(({ value, label }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setLeadSideOverride(value)}
                      disabled={isInProgress}
                      className={`px-3 py-1.5 rounded-sm-control text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        leadSideOverride === value
                          ? 'bg-accent text-text-inverse border-accent'
                          : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Evaluate / Progress Card */}
        <Card>
          <CardContent className="p-6">
            {/* Status: no video selected — info only, not a warning (user hasn't done anything wrong) */}
            {!athleteVideoId && !isProcessing && !isInProgress && (
              <Alert variant="info" className="mb-4">
                Upload a video of the athlete performing the movement to get started.
              </Alert>
            )}

            {/* Status: video uploading / processing */}
            {isProcessing && !isInProgress && (
              <Alert variant="info" className="mb-4">
                Processing video...
              </Alert>
            )}

            {/* Status: ready */}
            {athleteVideoId && !isProcessing && !isInProgress && !submitError && (
              <Alert variant="success" className="mb-4">
                Video selected! Ready to evaluate.
              </Alert>
            )}

            {/* Submit error — S28: branched on errorCode for distinct copy + CTA */}
            {submitError && !isInProgress && (
              <Alert variant="error" className="mb-4">
                <div className="space-y-2">
                  <p className="font-medium">Evaluation Failed</p>
                  <p className="text-sm">{submitError.message}</p>

                  {/* PRESERVED: isAuth login-redirect path */}
                  {submitError.isAuth && (
                    <p className="text-sm">
                      Redirecting to{' '}
                      <Link to="/login" className="underline text-accent">
                        login
                      </Link>
                      ...
                    </p>
                  )}

                  {/* S28: credit_balance_zero — membership-gated Buy CTA (D-S48)
                      Approved member → Buy-credits deep-link (S27 placeholder route).
                      Pending/non-member → Request access funnel.
                      no_allocation → contact-coach copy, NO buy CTA.
                      budget_exceeded → no CTA (legacy path). */}
                  {submitError.errorCode === 'credit_balance_zero' && (
                    isMemberApproved ? (
                      // Approved member: show Buy-credits CTA
                      // TODO(S27): replace href with S27's published buy-component route when shipped.
                      <a
                        data-testid="buy-credits-cta"
                        href={templateChannelSlug ? `/channels/${templateChannelSlug}?buy=1` : '#'}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent underline"
                      >
                        Buy a credit pack
                      </a>
                    ) : (
                      // Pending or non-member: funnel to request-to-join (D-S48 membership-first)
                      <span className="text-sm text-text-secondary">
                        Join this channel to purchase credits.{' '}
                        {templateChannelSlug && (
                          <Link
                            to={`/channels/${templateChannelSlug}`}
                            className="underline text-accent-blue"
                            data-testid="request-access-cta"
                          >
                            Request access
                          </Link>
                        )}
                      </span>
                    )
                  )}

                  {/* no_allocation: contact-coach copy, no buy CTA */}
                  {submitError.errorCode === 'no_allocation' && (
                    <p className="text-sm text-text-muted">
                      Contact your coach to have credits allocated to your account.
                    </p>
                  )}
                </div>
              </Alert>
            )}

            {/* Job-level error (from poll response).
                Quality-gate failures get friendly guidance + "Try again" CTA.
                All other errors fall back to showing the raw message. */}
            {job?.status === 'failed' && job.error_message && (() => {
              const qualityError = mapQualityError(job.error_message);
              if (qualityError) {
                return (
                  <Alert variant="warning" className="mb-4">
                    <div className="space-y-2">
                      <p className="font-medium">{qualityError.headline}</p>
                      {qualityError.tip && (
                        <p className="text-sm">{qualityError.tip}</p>
                      )}
                      {qualityError.showGuidance && (
                        <button
                          type="button"
                          onClick={() => {
                            // Reset job state and reopen guidance card so the
                            // user sees tips alongside the empty video selector.
                            if (templateId && athleteVideoId) {
                              clearStoredJobId(templateId, athleteVideoId);
                            }
                            setActiveJobId(null);
                            setAthleteVideoId('');
                            setSubmitError(null);
                            setGuidanceForceOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-2 text-warning hover:text-warning/80 transition-colors"
                          data-testid="quality-error-try-again"
                        >
                          Try again
                        </button>
                      )}
                    </div>
                  </Alert>
                );
              }
              return (
                <Alert variant="error" className="mb-4">
                  <div className="space-y-2">
                    <p className="font-medium">Evaluation Failed</p>
                    <p className="text-sm">{job.error_message}</p>
                  </div>
                </Alert>
              );
            })()}

            {/* Progress indicator — shown while submitting or polling.
                Uses EvaluationProgressCard (LoadingSection wrapper) for the
                labelled progress bar + elapsed counter + expected-duration hint.
                The stage checklist below provides per-step granularity. */}
            {isInProgress && (
              <div className="mb-4 space-y-3" aria-live="polite" aria-atomic="true">
                <EvaluationProgressCard
                  progressStage={isSubmitting ? undefined : job?.progress_stage}
                  progressPct={displayProgress}
                  startedAt={job?.submitted_at ? new Date(job.submitted_at) : undefined}
                />

                {/* Stage checklist */}
                <div className="space-y-2 text-xs text-text-secondary">
                  {(
                    [
                      ['pose_extraction', 'Extracting movement data'],
                      ['movement_preparation', 'Preparing the movement for comparison'],
                      ['scoring', 'Scoring movement form'],
                      ['coaching', 'Generating AI feedback'],
                      ['saving', 'Saving results'],
                    ] as const
                  ).map(([stage, label]) => {
                    const stageOrder = [
                      'pose_extraction',
                      'movement_preparation',
                      'scoring',
                      'coaching',
                      'saving',
                    ];
                    // A job queued before the 2026-10-07 rename still reports
                    // the old id "3d_lifting"; map it onto the same step so the
                    // checklist keeps a current row instead of showing none.
                    const reportedStage =
                      job?.progress_stage === '3d_lifting'
                        ? 'movement_preparation'
                        : job?.progress_stage;
                    const currentIdx = reportedStage
                      ? stageOrder.indexOf(reportedStage)
                      : -1;
                    const thisIdx = stageOrder.indexOf(stage);
                    const done = currentIdx > thisIdx;
                    const active = currentIdx === thisIdx;
                    return (
                      <div key={stage} className="flex items-center gap-2">
                        <span className={done || active ? 'text-success' : 'text-text-muted'}>
                          {done ? '✓' : active ? '→' : '○'}
                        </span>
                        <span className={done || active ? 'font-medium text-text-primary' : ''}>
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Person selection reminder (solo path only) */}
            {needsPersonSelection && !isInProgress && (
              <Alert variant="warning" className="mb-4">
                Multiple people detected. Please select the athlete above before evaluating.
              </Alert>
            )}

            {/* Positional: remind coach to confirm assignments */}
            {needsPositionalConfirmation && !isInProgress && (
              <Alert variant="warning" className="mb-4">
                Confirm position assignments above before evaluating.
              </Alert>
            )}

            {/* Frontal template: handedness declaration required before submit */}
            {needsHandednessSelection && !isInProgress && (
              <div data-testid="frontal-handedness-required-alert">
                <Alert variant="warning" className="mb-4">
                  Frontal-view template — select your handedness (Lead Side above) so your
                  video is oriented correctly; automatic detection is unreliable from the
                  front.
                </Alert>
              </div>
            )}

            {/* S28 pre-submit zero-balance gate — shown when balance is KNOWN zero.
                Only displayed when S26 data is loaded (isKnownZeroBalance).
                Never shown when loading/unknown (avoids false-positive gating). */}
            {isKnownZeroBalance && !isInProgress && !submitError && (
              <Alert variant="warning" className="mb-4">
                <div className="space-y-2">
                  <p className="font-medium text-sm">No evaluation credits available</p>
                  <p className="text-sm text-text-secondary">
                    You have no remaining credits for this channel.
                  </p>
                  {isMemberApproved ? (
                    // TODO(S27): replace href with S27's published buy-component route when shipped.
                    <a
                      data-testid="buy-credits-cta"
                      href={templateChannelSlug ? `/channels/${templateChannelSlug}?buy=1` : '#'}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent underline"
                    >
                      Buy a credit pack
                    </a>
                  ) : (
                    <span className="text-sm text-text-secondary">
                      {templateChannelSlug && (
                        <Link
                          to={`/channels/${templateChannelSlug}`}
                          className="underline text-accent-blue"
                          data-testid="request-access-cta"
                        >
                          Request access
                        </Link>
                      )}{' '}
                      to this channel to purchase credits.
                    </span>
                  )}
                </div>
              </Alert>
            )}

            {/* Credit balance widget — self-readable S26 balance near submit button.
                Degrades to null when hook unavailable (flag off / error). */}
            {!isInProgress && templateChannelSlug && (
              <div className="mb-4">
                <CreditBalanceWidget slug={templateChannelSlug} />
              </div>
            )}

            {/* Primary action button — S28: also disabled when balance is known zero */}
            <Button
              onClick={handleEvaluate}
              disabled={
                !athleteVideoId ||
                isProcessing ||
                isInProgress ||
                isPreflighting ||
                needsPersonSelection ||
                // Frontal template: block until a side is declared (required field)
                needsHandednessSelection ||
                // PTD-6: positional path requires confirmed assignments
                (isPositional && !positionAssignments) ||
                (job?.status === 'failed' && !canRetry) ||
                isKnownZeroBalance
              }
              className="w-full bg-accent hover:bg-accent-hover text-text-inverse disabled:opacity-50 disabled:cursor-not-allowed"
              size="lg"
            >
              {isPreflighting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Checking your video…
                </>
              ) : isInProgress ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {isSubmitting ? 'Submitting...' : 'Evaluating Performance...'}
                </>
              ) : job?.status === 'failed' && canRetry ? (
                'Retry Evaluation'
              ) : (
                'Evaluate Performance'
              )}
            </Button>

            {/* Help text */}
            {!athleteVideoId && (
              <p className="text-sm text-text-muted text-center mt-3">
                Select a video above to enable evaluation
              </p>
            )}

            {isInProgress && (
              <p className="text-sm text-text-muted text-center mt-3">
                This may take a minute. You can leave this page — come back to{' '}
                <Link to="/evaluations" className="underline text-accent">
                  Evaluations
                </Link>{' '}
                to see results.
              </p>
            )}

            {/* Slow operation banner — appears after 90 s of queued/processing state */}
            <SlowOperationBanner
              active={isInProgress}
              thresholdSeconds={90}
              className="mt-4"
            />
          </CardContent>
        </Card>
      </div>

      {/* Non-blocking pre-flight dialog (B-2026-0089 partial-body +
          Stance Orientation Tier 3 mismatch).

          Two paths based on what triggered the dialog:

          PATH A — "info" orientation only (no partial-body regions):
            Confident mismatch that the pipeline already auto-corrected.
            Render a REASSURING info modal — NOT a warning, NO "Re-record" CTA.
            Primary action is "Got it — submit".

          PATH B — advisory orientation, OR partial-body regions (with any orientation):
            Existing warning treatment: "Submit anyway" / "Re-record".
      */}

      {/* PATH A — reassuring info note (auto-corrected orientation, no body regions) */}
      {partialBodyWarning !== null &&
        partialBodyWarning.regions.length === 0 &&
        partialBodyWarning.orientationSeverity === 'info' && (
        <Modal
          isOpen
          onClose={() => {
            // Closing still proceeds with submit (user is just acknowledging)
            if (partialBodyWarning) {
              const { payload } = partialBodyWarning;
              setPartialBodyWarning(null);
              submitMutation.mutate(payload);
            }
          }}
          title="Orientation auto-corrected"
          size="sm"
        >
          <div className="space-y-4" data-testid="orientation-info-dialog">
            <Alert variant="info">
              {partialBodyWarning.orientationMessage}
            </Alert>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (partialBodyWarning) {
                    const { payload } = partialBodyWarning;
                    setPartialBodyWarning(null);
                    submitMutation.mutate(payload);
                  }
                }}
                data-testid="orientation-info-confirm"
              >
                Got it — submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* PATH B — warning dialog: advisory orientation and/or partial-body regions */}
      {partialBodyWarning !== null &&
        (partialBodyWarning.regions.length > 0 ||
          partialBodyWarning.orientationSeverity !== 'info') && (
        <ConfirmationDialog
          isOpen
          onClose={() => setPartialBodyWarning(null)}
          onConfirm={() => {
            if (partialBodyWarning) {
              const { payload } = partialBodyWarning;
              setPartialBodyWarning(null);
              submitMutation.mutate(payload);
            }
          }}
          title={
            partialBodyWarning.regions.length === 0
              ? 'Check your stance orientation'
              : 'Before you submit'
          }
          description={(() => {
            const parts: string[] = [];
            if (partialBodyWarning.regions.length > 0) {
              parts.push(
                `These body regions aren't fully visible and won't be scored: ${partialBodyWarning.regions
                  .map((r) => r.toLowerCase())
                  .join(', ')}. Re-record with your full body in frame for a complete evaluation.`
              );
            }
            if (partialBodyWarning.orientationMessage) {
              parts.push(partialBodyWarning.orientationMessage);
            }
            return parts.join(' ');
          })()}
          confirmLabel="Submit anyway"
          cancelLabel="Re-record"
          variant="warning"
        />
      )}
    </div>
  );
}

export default EvaluatePerformance;
